import { useTheme } from "@mui/material/styles"
import useMediaQuery from "@mui/material/useMediaQuery"
import { format as dfFormatDate } from "date-fns"
import parsePhoneNumber from "libphonenumber-js"
import { useCallback, useEffect, useState } from "react"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

export function formatNumber(n) {
  n = n || 0
  return Number(n).toLocaleString()
}

export function objectNullValuesToEmptyString(o) {
  const notNullsData = {}
  for (const key in o) {
    let value = o[key] === null ? "" : o[key]
    notNullsData[key] = value
  }
  return notNullsData
}

export function useIsMobile() {
  const theme = useTheme()
  return useMediaQuery(theme.breakpoints.only("xs"))
}

export function trimLongString(s: String, limit: Number = 50) {
  if (!s || s.length <= limit) {
    return s
  }
  return s.substring(0, limit) + ".."
}

export function formatCurrency(value, addDollarSign = true) {
  let suffix, divisor
  const absValue = Math.abs(value)
  if (!value || absValue < 1000) {
    suffix = ""
    divisor = 1
  } else if (absValue < 1e6) {
    suffix = "K"
    divisor = 1e3
  } else if (absValue < 1e9) {
    suffix = "M"
    divisor = 1e6
  } else {
    suffix = "B"
    divisor = 1e9
  }
  return `${addDollarSign ? "$" : ""}${(
    Math.round((value * 10) / divisor) / 10
  ).toLocaleString()}${suffix}`
}

export function formatCurrencyLong(amount) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

export function transactionSoldPriceDisplay(transaction) {
  return `${formatCurrencyLong(
    transaction.sold_price || transaction.lease_price
  )}${transaction.is_rental ? "/mo" : ""}`
}

export function validatePositive(newValue, valueSetter) {
  const numericNewValue = parseFloat(newValue)
  if (newValue === "" || numericNewValue >= 0) {
    valueSetter(newValue)
  }
}

export function environmentIsDevelop() {
  return process.env.REACT_APP_ENVIRONMENT === "develop"
}

export function environmentIsProduction() {
  return process.env.REACT_APP_ENVIRONMENT === "production"
}
const DATE_FORMAT = "MMMM do yyyy"

export function formatDate(dateStr) {
  return dfFormatDate(new Date(dateStr), DATE_FORMAT)
}

export function formatDateTime(date: str) {
  return dfFormatDate(new Date(date), `${DATE_FORMAT}, HH:mm aa`)
}

export function formatPhone(phone) {
  const parsedPhoneNumber = parsePhoneNumber(phone)
  return parsedPhoneNumber ? parsedPhoneNumber.formatNational() : phone
}

export function validatePhone(phone) {
  const phoneRegex = new RegExp("^(\\+1)?\\d{10}$")
  return phone.match(phoneRegex)
}

export function getProfileFullName(profile) {
  const firstName = profile?.first_name || ""
  const lastName = profile?.last_name || ""
  const fullName = `${firstName} ${lastName}`.trim()
  return fullName || profile?.email || ""
}

export function setErrorFromAPIArray(responseData, propertyName, setError) {
  const error = responseData[propertyName]
  if (error) {
    setError(error[0])
  }
}

export function firstApiErrorMessage(data, fallback = "Something went wrong.") {
  if (!data) {
    return fallback
  }
  if (typeof data === "string" && data.trim()) {
    return data.trim()
  }
  if (Array.isArray(data) && data.length) {
    return firstApiErrorMessage(data[0], fallback)
  }
  if (typeof data !== "object") {
    return fallback
  }
  for (const key of ["detail", "message", "error", "non_field_errors"]) {
    const value = data[key]
    if (typeof value === "string" && value.trim()) {
      return value.trim()
    }
    if (Array.isArray(value) && value.length) {
      return firstApiErrorMessage(value[0], fallback)
    }
    if (value && typeof value === "object") {
      if (typeof value.string === "string" && value.string.trim()) {
        return value.string.trim()
      }
      if (typeof value.detail === "string" && value.detail.trim()) {
        return value.detail.trim()
      }
    }
  }
  return fallback
}

export function useSearchParamsState(paramName) {
  const searchParams = useSearchParams()[0]
  const paramValue = searchParams.get(paramName)
  const [state, setState] = useState(paramValue || "")

  useEffect(() => {
    if (paramValue) {
      setState(paramValue)
    }
  }, [paramValue])

  return [state, setState]
}

export function useNavigateUpdatedSearchParams() {
  // useNavigate instead of updating searchParams which cause navigation
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()

  return useCallback(
    (valueMap) => {
      const next = new URLSearchParams(searchParams)
      Object.entries(valueMap).forEach(([paramName, paramValue]) => {
        if (
          paramValue === undefined ||
          paramValue === null ||
          paramValue === ""
        ) {
          next.delete(paramName)
        } else {
          next.set(paramName, String(paramValue))
        }
      })
      const query = next.toString()
      navigate(query ? `${location.pathname}?${query}` : location.pathname, {
        replace: true,
      })
    },
    [searchParams, location.pathname, navigate]
  )
}

export function useSimpleSearchParamState(paramName, defaultValue) {
  const searchParams = useSearchParams()[0]
  let initialValue = searchParams.get(paramName)
  if (initialValue && typeof defaultValue === "number") {
    initialValue = parseInt(initialValue)
  }
  const [state, setState] = useState(initialValue || defaultValue)
  return [state, setState]
}

export function useAutoNavigateSearchParamState(paramName, defaultValue) {
  const [state, setState] = useSimpleSearchParamState(paramName, defaultValue)
  const navigateUpdatedSearchParams = useNavigateUpdatedSearchParams()

  function updateStateAndNavigate(state) {
    setState(state)
    navigateUpdatedSearchParams({ [paramName]: state })
  }

  return [state, updateStateAndNavigate]
}
